import { defineStore } from "pinia";
import {
  getUserInfo,
  login as loginApi,
  logout as logoutApi,
} from "@/api/modules/auth";
import { getFirstAccessibleMenuPath } from "@/router/menuRoutes";

const USER_KEY = "moseycat-admin:user";
const LOGIN_KEY = "moseycat-admin:logged-in";

let restoreSessionPromise = null;

const normalizeArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  return value === null || value === undefined || value === "" ? [] : [value];
};

const readStoredUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem(USER_KEY) || "null");
  } catch {
    return null;
  }
};

const readLoginFlag = () => {
  try {
    return sessionStorage.getItem(LOGIN_KEY) === "1";
  } catch {
    return false;
  }
};

const writeSession = (user) => {
  try {
    sessionStorage.setItem(USER_KEY, JSON.stringify(user));
    sessionStorage.setItem(LOGIN_KEY, "1");
  } catch {
    // 浏览器禁用存储时仍保留当前内存会话，刷新页面后再由 /auth/me 恢复。
  }
};

const removeStoredSession = () => {
  try {
    sessionStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(LOGIN_KEY);
  } catch {
    // 清理内存状态已经足以退出当前页面，存储不可用时无需阻断退出流程。
  }
};

const sortMenus = (menus = []) =>
  normalizeArray(menus)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((menu) => ({
      ...menu,
      children: sortMenus(menu.children || []),
    }));

const normalizeUser = (payload, fallback = {}) => {
  const source = payload?.user || payload || {};

  if (typeof source === "string") {
    return {
      username: source,
      realName: source,
      roles: [],
      permissions: [],
      menus: [],
    };
  }

  const username = source.username || source.account || fallback.username || "";
  const menus = sortMenus(source.menus || source.menuList || []);

  return {
    id: source.id || source.userId || "",
    username,
    realName: source.realName || source.name || username,
    email: source.email || "",
    phone: source.phone || "",
    avatar: source.avatar || source.avatarUrl || "",
    roles: normalizeArray(source.roles || source.roleList),
    permissions: normalizeArray(source.permissions || source.permissionList),
    menus,
  };
};

export const useUserStore = defineStore("user", {
  state: () => ({
    user: readStoredUser(),
    loggedIn: readLoginFlag(),
    checkingSession: false,
  }),

  getters: {
    displayName: (state) =>
      state.user?.realName || state.user?.username || "User",
    menus: (state) => state.user?.menus || [],
    permissions: (state) => state.user?.permissions || [],
    roles: (state) => state.user?.roles || [],
    hasRole: (state) => (roleCode) => {
      const roles = state.user?.roles || [];

      return roles.some((role) => {
        if (typeof role === "string") {
          return role === roleCode;
        }

        return role?.roleCode === roleCode || role?.code === roleCode;
      });
    },
    defaultMenuPath: (state) =>
      getFirstAccessibleMenuPath(
        state.user?.menus || [],
        state.user?.permissions || [],
      ),
    hasPermission: (state) => (permission) => {
      if (!permission) {
        return true;
      }

      const permissions = state.user?.permissions || [];
      const required = Array.isArray(permission) ? permission : [permission];

      return (
        permissions.includes("*:*:*") ||
        required.some((item) => permissions.includes(item))
      );
    },
  },

  actions: {
    persistSession(user) {
      this.user = user;
      this.loggedIn = true;
      writeSession(user);
    },

    clearSession() {
      this.user = null;
      this.loggedIn = false;
      removeStoredSession();
    },

    async login(form) {
      const result = await loginApi({
        username: form.username,
        password: form.password,
        captchaKey: form.captchaKey,
        captchaCode: form.captchaCode,
      });

      this.persistSession(normalizeUser(result, { username: form.username }));

      return result;
    },

    async restoreSession() {
      if (restoreSessionPromise) {
        return restoreSessionPromise;
      }

      this.checkingSession = true;
      restoreSessionPromise = getUserInfo()
        .then((result) => {
          this.persistSession(normalizeUser(result));
          return true;
        })
        .catch(() => {
          this.clearSession();
          return false;
        })
        .finally(() => {
          this.checkingSession = false;
          restoreSessionPromise = null;
        });

      return restoreSessionPromise;
    },

    async logout() {
      try {
        await logoutApi();
      } finally {
        this.clearSession();
      }
    },
  },
});
